import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  makeStyles,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import LqaSubmissionDetailDrawer from './LqaSubmissionDetailDrawer';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  },
  header: {
    background: 'linear-gradient(45deg, #516e76 30%, #accacd 90%)',
    color: theme.palette.common.white,
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)'
  },
  headerTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 0)
  },
  tabsContainer: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[1]
  },
  tableContainer: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2]
  },
  tableHead: {
    backgroundColor: theme.palette.primary.main,
    '& .MuiTableCell-head': {
      color: theme.palette.common.white,
      fontWeight: 600
    }
  },
  statusChip: {
    color: theme.palette.common.white,
    fontWeight: 600
  },
  placeholder: {
    padding: theme.spacing(4),
    textAlign: 'center'
  },
  actionsCell: {
    whiteSpace: 'nowrap'
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  summaryCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[1]
  },
  summaryValue: {
    fontSize: '2rem',
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  summaryLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  }
}));

const statusColor = (theme, status) => {
  switch (status) {
    case 'success':
      return theme.palette.success.main;
    case 'failed':
      return theme.palette.error.main;
    default:
      return theme.palette.warning.main;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  return moment(value).format('MMM D, YYYY h:mm A');
};

const MyLqaView = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchMyLqaData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get('/api/aspire/lqa/my-lqa');
      if (data?.success) {
        setSubmissions(data.submissions || []);
        setDrafts(data.drafts || []);
      } else {
        setError(data?.message || 'Failed to load your LQA data.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load your LQA data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyLqaData();
  }, [fetchMyLqaData]);

  const handleRefresh = useCallback(() => {
    fetchMyLqaData();
  }, [fetchMyLqaData]);

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleSubmissionSelect = useCallback((submission) => {
    setSelectedSubmission(submission);
    setDetailOpen(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailOpen(false);
  }, []);

  const updateSubmissionInList = useCallback((updatedSubmission) => {
    setSubmissions((prev) => {
      const exists = prev.some((entry) => entry._id === updatedSubmission._id);
      if (!exists) {
        return [updatedSubmission, ...prev];
      }
      return prev.map((entry) => (entry._id === updatedSubmission._id ? updatedSubmission : entry));
    });
  }, []);

  const handleResubmit = useCallback(async (submissionId, payload) => {
    setResubmitting(true);
    try {
      const { data } = await axios.post(`/api/aspire/lqa/submissions/${submissionId}/resubmit`, payload);
      if (data?.success && data.submission) {
        updateSubmissionInList(data.submission);
        setSelectedSubmission(data.submission);
        setSnackbar({ open: true, severity: 'success', message: data.message || 'LQA resubmitted successfully.' });
        return data;
      }
      const message = data?.message || 'Failed to resubmit LQA.';
      setSnackbar({ open: true, severity: 'error', message });
      throw new Error(message);
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Failed to resubmit LQA.';
      setSnackbar({ open: true, severity: 'error', message });
      throw new Error(message);
    } finally {
      setResubmitting(false);
    }
  }, [updateSubmissionInList]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const renderStatusChip = useCallback((status) => (
    <Chip
      size="small"
      label={status ? status.toUpperCase() : 'UNKNOWN'}
      className={classes.statusChip}
      style={{ backgroundColor: statusColor(theme, status) }}
    />
  ), [classes.statusChip, theme]);

  const submissionStats = useMemo(() => {
    const total = submissions.length;
    const success = submissions.filter(s => s.status === 'success').length;
    const failed = submissions.filter(s => s.status === 'failed').length;
    const pending = submissions.filter(s => s.status === 'pending').length;
    return { total, success, failed, pending };
  }, [submissions]);

  const draftStats = useMemo(() => {
    return {
      total: drafts.length
    };
  }, [drafts]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4" className={classes.headerTitle}>My LQA Records</Typography>
        <Typography variant="body1">
          View your LQA submissions that have been sent to Aspire and drafts that are saved for later.
        </Typography>
      </div>

      <div className={classes.summaryCards}>
        <Paper className={classes.summaryCard}>
          <div className={classes.summaryValue}>{submissionStats.total}</div>
          <div className={classes.summaryLabel}>Total Submissions</div>
        </Paper>
        <Paper className={classes.summaryCard}>
          <div className={classes.summaryValue} style={{ color: theme.palette.success.main }}>
            {submissionStats.success}
          </div>
          <div className={classes.summaryLabel}>Successful</div>
        </Paper>
        <Paper className={classes.summaryCard}>
          <div className={classes.summaryValue} style={{ color: theme.palette.error.main }}>
            {submissionStats.failed}
          </div>
          <div className={classes.summaryLabel}>Failed</div>
        </Paper>
        <Paper className={classes.summaryCard}>
          <div className={classes.summaryValue} style={{ color: theme.palette.warning.main }}>
            {submissionStats.pending}
          </div>
          <div className={classes.summaryLabel}>Pending</div>
        </Paper>
        <Paper className={classes.summaryCard}>
          <div className={classes.summaryValue}>{draftStats.total}</div>
          <div className={classes.summaryLabel}>Saved Drafts</div>
        </Paper>
      </div>

      <Paper className={classes.tabsContainer}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={`Submissions (${submissions.length})`} />
          <Tab label={`Drafts (${drafts.length})`} />
        </Tabs>
      </Paper>

      <Toolbar className={classes.toolbar}>
        <Typography variant="h6">
          {tabValue === 0 ? 'My Submissions' : 'My Drafts'}
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={handleRefresh} disabled={loading} color="primary">
              {loading ? <CircularProgress size={22} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Toolbar>

      {loading && <LinearProgress />}

      {error && (
        <Box mb={2}>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        </Box>
      )}

      {tabValue === 0 && (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Job Site</TableCell>
                <TableCell>Property ID</TableCell>
                <TableCell>Submitted At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Attempts</TableCell>
                <TableCell>Last Completed</TableCell>
                <TableCell className={classes.actionsCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className={classes.placeholder}>
                      <Typography variant="body2" color="textSecondary">
                        You have not submitted any LQAs yet.
                      </Typography>
                      <Button color="primary" onClick={handleRefresh} style={{ marginTop: 16 }}>
                        Refresh
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                submissions.map((submission) => {
                  const attempts = (submission.resubmissionCount || 0) + 1;
                  const completedAt = submission.automation?.completedAt || submission.updatedAt;
                  return (
                    <TableRow
                      hover
                      key={submission._id}
                      onClick={() => handleSubmissionSelect(submission)}
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {submission.jobSiteName || submission.payloadSnapshot?.jobSite?.name || '—'}
                        </Typography>
                        {(submission.address || submission.payloadSnapshot?.jobSite?.address) && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {submission.address || submission.payloadSnapshot?.jobSite?.address}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{submission.propertyId || submission.payloadSnapshot?.propertyId || '—'}</TableCell>
                      <TableCell>{formatDate(submission.submittedAt || submission.createdAt)}</TableCell>
                      <TableCell>{renderStatusChip(submission.status)}</TableCell>
                      <TableCell>{attempts}</TableCell>
                      <TableCell>{formatDate(completedAt)}</TableCell>
                      <TableCell className={classes.actionsCell} onClick={(event) => event.stopPropagation()}>
                        <Tooltip title="View Details">
                          <IconButton color="primary" onClick={() => handleSubmissionSelect(submission)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tabValue === 1 && (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Job Site</TableCell>
                <TableCell>Crew</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Last Saved</TableCell>
                <TableCell>Saved By</TableCell>
                <TableCell className={classes.actionsCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drafts.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className={classes.placeholder}>
                      <Typography variant="body2" color="textSecondary">
                        You have no saved LQA drafts.
                      </Typography>
                      <Button color="primary" onClick={handleRefresh} style={{ marginTop: 16 }}>
                        Refresh
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                drafts.map((draft) => (
                  <TableRow hover key={draft._id} style={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {draft.jobSiteName || draft.jobSiteId?.name || '—'}
                      </Typography>
                      {draft.jobSiteId?.address && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {draft.jobSiteId.address}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{draft.crewName || draft.crewId?.name || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={draft.type === 'crew_lqa' ? 'Crew LQA' : draft.type || 'Unknown'}
                        color="default"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        {draft.progress || 'Not set'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(draft.updated_at || draft.savedAt)}</TableCell>
                    <TableCell>
                      {draft.savedByName ||
                       draft.savedBy?.displayName ||
                       `${draft.savedBy?.firstName || ''} ${draft.savedBy?.lastName || ''}`.trim() ||
                       '—'}
                    </TableCell>
                    <TableCell className={classes.actionsCell}>
                      <Tooltip title="Edit Draft">
                        <IconButton color="primary">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <LqaSubmissionDetailDrawer
        open={detailOpen}
        submission={selectedSubmission}
        onClose={handleCloseDetail}
        onResubmit={handleResubmit}
        resubmitting={resubmitting}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} elevation={6} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default MyLqaView;
